/* ──────────────────────────────────────────────
 *  État d'action d'une carte prestation (étape « prestations »)
 *
 *  Une carte n'est jamais bloquée À CAUSE du panier : si la
 *  prestation est réservable seule mais pas avec la sélection
 *  courante, elle propose de REMPLACER la sélection et dit
 *  pourquoi. Seule une indisponibilité réelle (aucun créneau,
 *  aucun lieu) grise la carte.
 * ────────────────────────────────────────────── */

import { isAvailablePriceOption } from '@cfixe-monorepo/shared';
import type { BookingPrestation, CartItem } from '../types';
import { isPrestationPlaceCompatible } from './place-compat';

/** Pourquoi l'action remplace la sélection : multi-prestation OFF, lieu ou créneau incompatible. */
export type ReplaceReason = 'single' | 'place' | 'slot';

export interface ServiceCardState {
  /**
   * Version de la prestation sur laquelle la carte se configure. En conflit avec le panier, c'est
   * celle du chargement initial (sondée sans panier) : c'est elle qui vaudra une fois la sélection
   * remplacée, alors que la version courante porte des durées masquées à cause du panier.
   */
  source: BookingPrestation;
  /** La carte propose une action (ajout, remplacement ou mise à jour). */
  actionable: boolean;
  /** Présent quand l'action remplace la sélection courante. */
  replaceReason?: ReplaceReason;
}

const isBookableWith = (prestation: BookingPrestation, cartItems: CartItem[], placeIds: number[]): boolean =>
  prestation.is_bookable !== false &&
  isPrestationPlaceCompatible(prestation.pro_place_ids, cartItems, placeIds) &&
  prestation.prices.some(isAvailablePriceOption);

export function resolveServiceCardState(params: {
  prestation: BookingPrestation;
  /** Même prestation telle que chargée sans panier (`baselinePrestations`). */
  baseline: BookingPrestation | undefined;
  cartItems: CartItem[];
  placeIds: number[];
  singleSelection: boolean;
}): ServiceCardState {
  const { prestation, cartItems, placeIds, singleSelection } = params;
  const hasCart = cartItems.length > 0;

  /* Multi-prestation OFF : la sélection est toujours remplacée, donc jugée contre un panier vide
     (pas de recalcul live dans ce mode). La prestation déjà choisie se met à jour, sans remplacer. */
  if (singleSelection) {
    const actionable = isBookableWith(prestation, [], placeIds);
    const isSelected = cartItems.some((i): boolean => i.prestation.prestation_id === prestation.prestation_id);
    return {
      source: prestation,
      actionable,
      ...(actionable && hasCart && !isSelected ? { replaceReason: 'single' as const } : {}),
    };
  }

  if (isBookableWith(prestation, cartItems, placeIds)) {
    return { source: prestation, actionable: true };
  }
  if (!hasCart) {
    return { source: prestation, actionable: false };
  }

  /* Bloquée avec le panier : réservable seule ? Sinon l'indisponibilité est réelle. */
  const baseline = params.baseline ?? prestation;
  if (!isBookableWith(baseline, [], placeIds)) {
    return { source: prestation, actionable: false };
  }
  /* Déjà dans le panier : jamais « en conflit » avec lui (le recalcul live ne la sonde pas). */
  if (cartItems.some((i): boolean => i.prestation.prestation_id === prestation.prestation_id)) {
    return { source: baseline, actionable: true };
  }
  const placeCompatible = isPrestationPlaceCompatible(prestation.pro_place_ids, cartItems, placeIds);
  return { source: baseline, actionable: true, replaceReason: placeCompatible ? 'slot' : 'place' };
}
